import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, ClipboardList, Heart, TrendingUp, User, Activity } from 'lucide-react';
import { ComprehensiveUserProfile } from './user-profile/types';
import { OverviewTab } from './user-profile/OverviewTab';
import { AssessmentsTab } from './user-profile/AssessmentsTab';
import { HealthTab } from './user-profile/HealthTab';
import { EngagementTab } from './user-profile/EngagementTab';
import { ProfileTab } from './user-profile/ProfileTab';
import { ActivityTab } from './user-profile/ActivityTab';

interface UserDetailPanelProps {
  userId: string;
}

export const UserDetailPanel: React.FC<UserDetailPanelProps> = ({ userId }) => {
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['user-full-profile-v2', userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_app_user_full_profile_v2', { p_user_id: userId });
      if (error) throw error;
      return data as unknown as ComprehensiveUserProfile;
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 bg-muted/30 border-t border-border">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="p-6 bg-muted/30 border-t border-border">
        <p className="text-destructive">Error loading user profile</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-muted/30 border-t border-border">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="assessments" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Assessments</span>
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Health</span>
          </TabsTrigger>
          <TabsTrigger value="engagement" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Engagement</span>
          </TabsTrigger>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Activity</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab profile={profile} /></TabsContent>
        <TabsContent value="assessments"><AssessmentsTab profile={profile} /></TabsContent>
        <TabsContent value="health"><HealthTab profile={profile} /></TabsContent>
        <TabsContent value="engagement"><EngagementTab profile={profile} /></TabsContent>
        <TabsContent value="profile"><ProfileTab profile={profile} /></TabsContent>
        <TabsContent value="activity"><ActivityTab profile={profile} /></TabsContent>
      </Tabs>
    </div>
  );
};
